/**
 * 📊 Dashboard Component
 * Hauptübersicht des iSendInbox Systems
 */

import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { 
  Mail, 
  Server, 
  FileText, 
  Activity, 
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  Layers
} from 'lucide-react';
import axios from 'axios';
import toast from 'react-hot-toast';
import CreateJob from '../Jobs/CreateJob';
import MultiCampaignCreator from '../MultiCampaign/MultiCampaignCreator';

const Dashboard = () => {
  const [showCreateJob, setShowCreateJob] = useState(false);
  const [showMultiCampaignCreator, setShowMultiCampaignCreator] = useState(false);

  // Check for job reuse parameters and create parameter
  React.useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('reuse') === 'true' || urlParams.get('create') === 'true') {
      setShowCreateJob(true);
    }
  }, []);

  // Fetch system stats
  const { data: systemStats, isLoading } = useQuery(
    'systemStats',
    async () => {
      const response = await axios.get('/api/jobs/stats');
      return response.data.data;
    },
    {
      refetchInterval: 10000, // Update every 10 seconds
    }
  );

  // Fetch active jobs
  const { data: activeJobs } = useQuery(
    'activeJobs',
    async () => {
      const response = await axios.get('/api/jobs/active/list');
      return response.data.data;
    },
    {
      refetchInterval: 5000, // Update every 5 seconds
    }
  );

  const { data: multiCampaigns } = useQuery(
    'dashboardMultiCampaigns',
    async () => {
      const response = await axios.get('/api/multi-campaigns');
      return response.data.data;
    },
    {
      refetchInterval: 10000,
    }
  );

  // Fetch recent jobs
  const { data: recentJobs } = useQuery(
    'recentJobs',
    async () => {
      const response = await axios.get('/api/jobs?limit=10');
      return response.data.data;
    },
    {
      refetchInterval: 30000, // Update every 30 seconds
    }
  );

  // Combine jobs and multi-campaigns for unified display (same logic as MailJobs)
  const combinedItems = React.useMemo(() => {
    const items = [];
    
    if (recentJobs) {
      // Group multi-campaign jobs by campaign_id
      const multiCampaignJobs = recentJobs.filter(job => job.multi_campaign_id);
      const regularJobs = recentJobs.filter(job => !job.multi_campaign_id);
      
      // Group multi-campaign jobs
      const campaignGroups = {};
      multiCampaignJobs.forEach(job => {
        const campaignId = job.multi_campaign_id;
        if (!campaignGroups[campaignId]) {
          campaignGroups[campaignId] = {
            type: 'multi-campaign-group',
            campaign_id: campaignId,
            slots: [],
            total_recipients: 0,
            total_sent: 0,
            total_failed: 0,
            created_at: job.created_at,
            status: 'running'
          };
        }
        campaignGroups[campaignId].slots.push(job);
        campaignGroups[campaignId].total_recipients += (job.total_recipients || 0);
        campaignGroups[campaignId].total_sent += (job.sent_count || 0);
        campaignGroups[campaignId].total_failed += (job.failed_count || 0);
      });
      
      // Calculate status and add campaign info
      Object.values(campaignGroups).forEach(group => {
        const statuses = group.slots.map(slot => slot.status);
        if (statuses.every(s => s === 'completed')) {
          group.status = 'completed';
        } else if (statuses.some(s => s === 'processing' || s === 'running')) {
          group.status = 'running';
        } else if (statuses.some(s => s === 'failed')) {
          group.status = 'failed';
        } else {
          group.status = 'pending';
        }
        
        const campaignInfo = multiCampaigns?.find(mc => mc.id === group.campaign_id);
        if (campaignInfo) {
          group.name = campaignInfo.name;
          group.campaign_string_id = campaignInfo.campaign_id;
          group.subject = campaignInfo.name; // For display consistency
        } else {
          group.name = `Multi-Campaign ${group.campaign_id}`;
          group.subject = `Multi-Campaign ${group.campaign_id}`;
        }
      });
      
      // Add regular jobs
      items.push(...regularJobs.map(job => ({ ...job, type: 'job' })));
      
      // Add multi-campaign groups
      items.push(...Object.values(campaignGroups));
      
      // Add standalone multi-campaigns (Segmented-Parallel ohne Jobs!)
      if (multiCampaigns) {
        const existingCampaignIds = Object.keys(campaignGroups).map(id => parseInt(id));
        const standaloneCampaigns = multiCampaigns.filter(campaign => 
          !existingCampaignIds.includes(campaign.id)
        );
        
        standaloneCampaigns.forEach(campaign => {
          const isDraft = campaign.status === 'draft';
          const isRunning = ['running', 'processing', 'completed', 'failed', 'paused'].includes(campaign.status);
          
          items.push({
            type: isDraft ? 'multi-campaign-draft' : 'multi-campaign-group',
            campaign_id: campaign.id,
            campaign_string_id: campaign.campaign_id,
            name: campaign.name,
            subject: campaign.name,
            description: campaign.description,
            status: campaign.status,
            total_slots: campaign.total_slots || 0,
            total_recipients: campaign.total_recipients || 0,
            total_sent: campaign.total_sent || 0,
            total_failed: campaign.total_failed || 0,
            execution_mode: campaign.execution_mode,
            slots: [],
            is_aggregated: isRunning,
            created_at: campaign.created_at
          });
        });
      }
    }
    
    // Sort by creation date and limit to recent items
    return items.sort((a, b) => new Date(b.created_at) - new Date(a.created_at)).slice(0, 10);
  }, [recentJobs, multiCampaigns]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white dark:bg-dark-secondary shadow rounded-lg p-4 md:p-6 border dark:border-dark-copper-dark">
        <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-gray-100 mb-2 flex items-center font-bai-jamjuree">
              <Activity className="h-6 w-6 md:h-8 md:w-8 mr-2 md:mr-3 text-blue-600 dark:text-dark-copper-glow" />
              Dashboard
            </h1>
            <p className="text-sm md:text-base text-gray-600 dark:text-gray-200">
             Fummelboard
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 w-full md:w-auto">
            <button
              onClick={() => setShowCreateJob(true)}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md font-medium flex items-center justify-center space-x-2 w-full sm:w-auto"
            >
              <Mail className="h-4 w-4" />
              <span>Einzelner Job</span>
            </button>
            <button
              onClick={() => setShowMultiCampaignCreator(true)}
              className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-md font-medium flex items-center justify-center space-x-2 w-full sm:w-auto"
            >
              <Layers className="h-4 w-4" />
              <span>Multi-Campaign</span>
            </button>
          </div>
        </div>
      </div>


      {/* Create Job Form */}
      {showCreateJob && (
        <CreateJob onJobCreated={(job) => {
          setShowCreateJob(false);
          // Refresh data
          window.location.reload();
        }} />
      )}

      {/* Multi-Campaign Creator */}
      {showMultiCampaignCreator && (
        <MultiCampaignCreator
          onClose={() => setShowMultiCampaignCreator(false)}
          onSuccess={() => {
            setShowMultiCampaignCreator(false);
            toast.success('Multi-Campaign erfolgreich erstellt!');
            window.location.reload();
          }}
        />
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatsCard
          title="Aktive Jobs"
          value={activeJobs?.length || 0}
          icon={<Activity className="h-8 w-8 text-blue-600 dark:text-dark-copper-glow" />}
          color="blue"
        />
        
        <StatsCard
          title="Heute gesendet"
          value={systemStats?.totals?.total_recipients || 0}
          icon={<Mail className="h-8 w-8 text-green-600 dark:text-dark-copper-glow" />}
          color="green"
        />
        
        <StatsCard
          title="SMTP Server"
          value={systemStats?.smtp?.active_smtp_servers || 0}
          icon={<Server className="h-8 w-8 text-purple-600 dark:text-dark-copper-glow" />}
          color="purple"
        />
        
        <StatsCard
          title="Templates"
          value={systemStats?.templates?.total_templates || 0}
          icon={<FileText className="h-8 w-8 text-orange-600 dark:text-dark-copper-glow" />}
          color="orange"
        />
      </div>

      {/* Active Jobs */}
      {activeJobs && activeJobs.length > 0 && (
        <div className="bg-white dark:bg-dark-secondary shadow rounded-lg p-6 border dark:border-dark-copper-dark">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center">
            <Activity className="h-5 w-5 mr-2 text-blue-600" />
            Aktive Jobs
          </h2>
          
          <div className="space-y-3">
            {activeJobs.map((job) => (
              <div key={job.jobId} className="flex items-center justify-between p-4 bg-blue-50 dark:bg-dark-surface rounded-lg border dark:border-dark-copper-mid">
                <div>
                  <div className="font-medium text-gray-900 dark:text-gray-200">{job.jobId}</div>
                  <div className="text-sm text-gray-600 dark:text-gray-300">
                    {job.recipientCount} Empfänger • Läuft seit {Math.floor(job.runtime / 60000)} Min
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="px-2 py-1 bg-blue-100 dark:bg-dark-copper-mid text-blue-800 dark:text-dark-copper-light text-xs rounded-full">
                    {job.status}
                  </span>
                  <div className="w-3 h-3 bg-green-400 rounded-full animate-pulse"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recent Jobs */}
      <div className="bg-white dark:bg-dark-secondary shadow rounded-lg p-6 border dark:border-dark-copper-dark">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center">
          <Clock className="h-5 w-5 mr-2 text-gray-600" />
          Letzte Jobs
        </h2>
        
        {combinedItems && combinedItems.length > 0 ? (
          <div className="block">
            {/* Desktop Table */}
            <div className="hidden md:block">
              <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50 dark:bg-dark-surface">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-200 uppercase tracking-wider">
                    Job
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-200 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-200 uppercase tracking-wider">
                    Empfänger
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-200 uppercase tracking-wider">
                    Datum
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-dark-secondary divide-y divide-gray-200 dark:divide-dark-copper-dark">
                {combinedItems.map((item) => (
                  <tr 
                    key={item.job_id || item.campaign_id} 
                    className="hover:bg-gray-50 dark:hover:bg-dark-surface"
                  >
                    <td className="px-3 py-3 cursor-pointer" onClick={() => {
                      if (item.type === 'multi-campaign-group' || item.type === 'multi-campaign-draft') {
                        window.location.href = `/multi-campaigns/${item.campaign_string_id || item.campaign_id}`;
                      } else {
                        window.location.href = `/jobs/${item.job_id}`;
                      }
                    }}>
                      <div className="flex items-center">
                        {item.type === 'multi-campaign-group' || item.type === 'multi-campaign-draft' ? (
                          <Layers className="h-4 w-4 text-purple-600 mr-2" />
                        ) : (
                          <Mail className="h-4 w-4 text-blue-600 mr-2" />
                        )}
                        <div>
                          <div className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate max-w-xs">
                            {(item.subject || item.name || '').substring(0, 25)}
                            {(item.subject || item.name || '').length > 25 ? '...' : ''}
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-300 font-mono">
                            {item.type === 'multi-campaign-group' 
                              ? `${item.slots.length} Slots`
                              : item.type === 'multi-campaign-draft'
                                ? `Draft Campaign`
                                : `${(item.job_id || '').substring(0, 12)}...`
                            }
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-3 cursor-pointer" onClick={() => {
                      if (item.type === 'multi-campaign-group' || item.type === 'multi-campaign-draft') {
                        window.location.href = `/multi-campaigns/${item.campaign_string_id || item.campaign_id}`;
                      } else {
                        window.location.href = `/jobs/${item.job_id}`;
                      }
                    }}>
                      <StatusBadge status={item.status} />
                    </td>
                    <td className="px-3 py-3 text-sm text-gray-600 dark:text-gray-200 cursor-pointer" onClick={() => {
                      if (item.type === 'multi-campaign-group' || item.type === 'multi-campaign-draft') {
                        window.location.href = `/multi-campaigns/${item.campaign_string_id || item.campaign_id}`;
                      } else {
                        window.location.href = `/jobs/${item.job_id}`;
                      }
                    }}>
                      {item.total_recipients || item.recipient_count || 0}
                    </td>
                    <td className="px-3 py-3 text-xs text-gray-500 dark:text-gray-200 cursor-pointer" onClick={() => {
                      if (item.type === 'multi-campaign-group' || item.type === 'multi-campaign-draft') {
                        window.location.href = `/multi-campaigns/${item.campaign_string_id || item.campaign_id}`;
                      } else {
                        window.location.href = `/jobs/${item.job_id}`;
                      }
                    }}>
                      {new Date(item.created_at).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })}
                    </td>
                  </tr>
                ))}
              </tbody>
              </table>
            </div>
            
            {/* Mobile Cards */}
            <div className="md:hidden space-y-3">
              {combinedItems.map((item) => (
                <div 
                  key={item.job_id || item.campaign_id}
                  className={`border border-gray-200 dark:border-dark-copper-dark rounded-lg p-4 hover:bg-gray-50 dark:hover:bg-dark-surface cursor-pointer ${
                    item.type === 'multi-campaign-group' ? 'border-purple-200' : ''
                  }`}
                  onClick={() => {
                    if (item.type === 'multi-campaign-group') {
                      window.location.href = `/multi-campaigns/${item.campaign_string_id || item.campaign_id}`;
                    } else {
                      window.location.href = `/jobs/${item.job_id}`;
                    }
                  }}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center mb-1">
                        {item.type === 'multi-campaign-group' || item.type === 'multi-campaign-draft' ? (
                          <Layers className="h-4 w-4 text-purple-600 mr-2" />
                        ) : (
                          <Mail className="h-4 w-4 text-blue-600 mr-2" />
                        )}
                        <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                          {(item.subject || item.name || '').substring(0, 40)}
                          {(item.subject || item.name || '').length > 40 ? '...' : ''}
                        </h3>
                      </div>
                      <p className="text-xs text-gray-500 font-mono mt-1">
                        {item.type === 'multi-campaign-group' 
                          ? `${item.slots.length} Slots • ${item.total_recipients} Empfänger`
                          : item.type === 'multi-campaign-draft'
                          ? `${item.total_slots} Slots • ENTWURF • ${item.total_recipients} Empfänger`
                          : `${item.job_id.substring(0, 20)}...`
                        }
                      </p>
                    </div>
                    <StatusBadge status={item.status} />
                  </div>
                  
                  <div className="flex items-center justify-between text-xs text-gray-500">
                    <span>{item.total_recipients || item.recipient_count || 0} Empfänger</span>
                    <span>{new Date(item.created_at).toLocaleDateString('de-DE')}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            Noch keine Jobs erstellt
          </div>
        )}
      </div>
    </div>
  );
};

// Stats Card Component
const StatsCard = ({ title, value, icon, color }) => {
  const colorClasses = {
    blue: 'bg-blue-50 dark:bg-dark-surface border-blue-200 dark:border-dark-copper-mid',
    green: 'bg-green-50 dark:bg-dark-surface border-green-200 dark:border-dark-copper-mid',
    purple: 'bg-purple-50 dark:bg-dark-surface border-purple-200 dark:border-dark-copper-mid',
    orange: 'bg-orange-50 dark:bg-dark-surface border-orange-200 dark:border-dark-copper-mid'
  };

  return (
    <div className={`${colorClasses[color]} border rounded-lg p-6`}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600 dark:text-gray-200">{title}</p>
          <p className="text-3xl font-bold text-gray-900 dark:text-dark-copper-glow">{value}</p>
        </div>
        <div className="flex-shrink-0">
          {icon}
        </div>
      </div>
    </div>
  );
};

// Status Badge Component
const StatusBadge = ({ status }) => {
  const statusConfig = {
    pending: { color: 'bg-yellow-100 text-yellow-800', icon: <Clock className="h-3 w-3" /> },
    processing: { color: 'bg-blue-100 text-blue-800', icon: <Activity className="h-3 w-3" /> },
    paused: { color: 'bg-orange-100 text-orange-800', icon: <AlertCircle className="h-3 w-3" /> },
    completed: { color: 'bg-green-100 text-green-800', icon: <CheckCircle className="h-3 w-3" /> },
    failed: { color: 'bg-red-100 text-red-800', icon: <XCircle className="h-3 w-3" /> },
    stopped: { color: 'bg-gray-100 text-gray-800', icon: <AlertCircle className="h-3 w-3" /> }
  };

  const config = statusConfig[status] || statusConfig.pending;

  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
      {config.icon}
      <span className="ml-1">{status}</span>
    </span>
  );
};

// useAuth is imported from AuthService, not defined here

export default Dashboard;
